<template>
  <v-card id="add-credit-return-availability">
    <v-card-title class="i-card-title-div">
      <v-row class="align-center">
        <v-col cols="12" sm="9">
          <span class="g-bold g-heading-l"
            >{{ newCreditReturnAvailabilityRule ? 'New' : 'Edit' }} Credit
            Returns Rule</span
          >
        </v-col>
        <v-col cols="12" sm="3">
          <i-instructions
            instructionKey="data-maintenance/credit-returns/add-dialog"
          ></i-instructions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-8 i-card-content-div">
      <v-form v-model="form" @submit.prevent>
        <span class="g-bold g-heading-s g-text-color-black">Target Type</span>
        <div class="mb-6">
          <v-radio-group
            class="i-radio-group"
            :disabled="!newCreditReturnAvailabilityRule"
            @change="resetTargetTypeForm"
            v-model="targetType"
          >
            <v-row>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === ALL_GEOGRAPHY }"
                >
                  <v-radio
                    label="All (Geography)"
                    :value="ALL_GEOGRAPHY"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === RETAILER_STORE }"
                >
                  <v-radio
                    label="Retailer(s)"
                    :value="RETAILER_STORE"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetType === BANNER_GROUP }"
                >
                  <v-radio
                    label="Banner Group(s)"
                    :value="BANNER_GROUP"
                  ></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{
                    'item-div-active': targetType === MULTI_STORE_OPERATOR
                  }"
                >
                  <v-radio
                    label="Multi-Store Operator(s)"
                    :value="MULTI_STORE_OPERATOR"
                  ></v-radio>
                </div>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>

        <template v-if="targetType === RETAILER_STORE">
          <p class="g-bold g-heading-s g-text-color-black">
            {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
          </p>
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :items="targetStores"
            :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
            multiple
            outlined
            :rules="[
              (v) =>
                !!v.length ||
                `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
            ]"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetStoreIds"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>

        <template v-if="targetType === BANNER_GROUP">
          <span class="g-bold g-heading-s g-text-color-black mt-3 mt-md-5"
            >Banner Group(s)</span
          >
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :items="targetBannerGroups"
            label="Select Banner Group(s)"
            multiple
            outlined
            :rules="[(v) => !!v.length || 'Banner Group(s) required']"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetBannerGroupIds"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>

        <template v-if="targetType === MULTI_STORE_OPERATOR">
          <span class="g-bold g-heading-s g-text-color-black mt-3 mt-md-5"
            >Multi-Store Operator(s)</span
          >
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :items="targetMultiStoreOperators"
            label="Select Multi-Store Operator(s)"
            multiple
            outlined
            :rules="[(v) => !!v.length || 'Multi-Store Operator(s) required']"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetMultiStoreOperatorIds"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>

        <template v-if="targetType !== RETAILER_STORE">
          <span class="g-bold g-heading-s g-text-color-black mt-2"
            >Target Geography</span
          >
          <div class="mb-6">
            <v-radio-group
              class="i-radio-group"
              @change="resetTargetGeographyForm"
              v-model="targetGeography"
            >
              <v-row>
                <v-col cols="12" md="4" class="pb-0">
                  <div
                    class="item-div-mini mb-0"
                    :class="{ 'item-div-active': targetGeography === NATIONAL }"
                  >
                    <v-radio label="National" :value="NATIONAL"></v-radio>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pb-0">
                  <div
                    class="item-div-mini mb-0"
                    :class="{ 'item-div-active': targetGeography === STATE }"
                  >
                    <v-radio label="State(s)" :value="STATE"></v-radio>
                  </div>
                </v-col>
              </v-row>
            </v-radio-group>
          </div>

          <template v-if="targetGeography === STATE">
            <span class="g-bold g-heading-s g-text-color-black mt-2"
              >State(s)</span
            >
            <v-autocomplete
              attach
              background-color="#0000000f"
              class="mt-3 mb-6"
              clearable
              deletable-chips
              dense
              :items="states"
              label="Select State(s)"
              multiple
              outlined
              :rules="[
                (v) =>
                  !!v.length ||
                  'State(s) required for State-based Credit Returns rule'
              ]"
              v-model:search-input="searchInput"
              small-chips
              v-model="targetStoreDeliveryAddressStates"
              @change="searchInput = ''"
            ></v-autocomplete>
          </template>
        </template>
      </v-form>
    </v-card-text>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="$emit('cancel')"
        >Cancel</v-btn
      >
      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        :disabled="!validCreditReturnAvailabilityRule"
        :loading="saving"
        @click="saveCreditReturnAvailabilityRule"
        >{{ newCreditReturnAvailabilityRule ? 'Create' : 'Update' }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  addCreditReturnAvailabilityRule,
  updateCreditReturnAvailabilityRule
} from '@/services/creditReturnAvailability'
import FieldValidators from '@/utils/FieldValidators'
import iInstructions from '@/components/Instructions'

const RULE_FIELDS = {
  creditReturnAvailabilityRuleId: null,
  targetType: null,
  targetGeography: null,
  targetBannerGroupIds: [],
  targetMultiStoreOperatorIds: [],
  targetStoreIds: [],
  targetStoreDeliveryAddressStates: []
}

const RULE_GEOGRAPHY = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE'
}

const RULE_TARGET_TYPE = {
  ALL_GEOGRAPHY: 'ALL_GEOGRAPHY',
  BANNER_GROUP: 'BANNER_GROUP',
  MULTI_STORE_OPERATOR: 'MULTI_STORE_OPERATOR',
  RETAILER_STORE: 'RETAILER_STORE'
}

export default {
  components: {
    iInstructions
  },
  props: {
    creditReturnAvailabilityRule: Object
  },

  data: () => ({
    form: false,
    saving: false,
    searchInput: '',
    originalCreditReturnAvailabilityRule: {},
    states: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
    targetMultiStoreOperators: [],
    targetBannerGroups: [],
    targetStores: [],
    ...FieldValidators,
    ...RULE_FIELDS,
    ...RULE_GEOGRAPHY,
    ...RULE_TARGET_TYPE
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', ['isILR']),

    newCreditReturnAvailabilityRule() {
      return _.isEmpty(this.creditReturnAvailabilityRule)
    },

    changesMade() {
      return !_.isEqual(
        this.originalCreditReturnAvailabilityRule,
        this.createBlueprint()
      )
    },

    validGeography() {
      return (
        this.targetType === this.RETAILER_STORE ||
        this.targetGeography === this.NATIONAL ||
        (this.targetGeography === this.STATE &&
          !!this.targetStoreDeliveryAddressStates.length)
      )
    },

    validTargetType() {
      return (
        this.targetType === this.ALL_GEOGRAPHY ||
        (this.targetType === this.RETAILER_STORE &&
          !!this.targetStoreIds.length) ||
        (this.targetType === this.BANNER_GROUP &&
          !!this.targetBannerGroupIds.length) ||
        (this.targetType === this.MULTI_STORE_OPERATOR &&
          !!this.targetMultiStoreOperatorIds.length)
      )
    },

    validCreditReturnAvailabilityRule() {
      return (
        this.validTargetType &&
        this.validGeography &&
        this.form &&
        this.changesMade
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // ***
    // Functions related to help determining if Special has been modified
    // ***
    createBlueprint() {
      return {
        creditReturnAvailabilityRuleId: this.creditReturnAvailabilityRuleId,
        targetGeography: this.targetGeography,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates,
        targetType: this.targetType
      }
    },

    // ***
    // Functions related to CreditReturnAvailabilityRule actions
    // ***

    createCreditReturnAvailabilityRule(payload) {
      addCreditReturnAvailabilityRule(this.selectedSupplierId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Credit returns rule has been created!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            default:
              msg =
                'Your credit returns rule could not be created, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    copyCreditReturnAvailabilityRule() {
      this.creditReturnAvailabilityRuleId = this.creditReturnAvailabilityRule.id
      this.targetGeography = this.creditReturnAvailabilityRule.targetGeography
      this.targetBannerGroupIds =
        this.creditReturnAvailabilityRule.targetBannerGroups?.map(
          (bannerGroup) => bannerGroup.bannerGroupId
        ) || []
      this.targetMultiStoreOperatorIds =
        this.creditReturnAvailabilityRule.targetMultiStoreOperators?.map(
          (mso) => mso.multiStoreOperatorId
        ) || []
      this.targetStoreIds =
        this.creditReturnAvailabilityRule.targetStores?.map(
          (store) => store.storeId
        ) || []
      this.targetStoreDeliveryAddressStates =
        this.creditReturnAvailabilityRule.targetStoreDeliveryAddressStates?.map(
          (state) => state.state
        ) || []
      this.targetType = this.determineTargetType()
    },

    updateCreditReturnAvailabilityRule(payload) {
      updateCreditReturnAvailabilityRule(
        this.selectedSupplierId,
        this.creditReturnAvailabilityRuleId,
        payload
      )
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Credit returns rule has been updated!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            default:
              msg =
                'Your credit returns rule could not be updated, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    saveCreditReturnAvailabilityRule() {
      this.saving = true

      const payload = {
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates
      }

      this.newCreditReturnAvailabilityRule
        ? this.createCreditReturnAvailabilityRule(payload)
        : this.updateCreditReturnAvailabilityRule(payload)
    },

    // ***
    // Functions related data mapping
    // ***

    mapBannerGroupsList() {
      // Map property names so that it works with V-select component
      this.targetBannerGroups = this.bannerGroups
        .map((bannerGroup) => ({
          text: bannerGroup.displayName,
          value: bannerGroup.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapMultiStoreOperatorsList() {
      // Map property names so that it works with V-select component
      this.targetMultiStoreOperators = this.multiStoreOperators
        .map((mso) => ({
          text: mso.displayName,
          value: mso.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetStores = this.retailerStores
          .map((store) => ({
            text: store.displayName,
            value: store.id
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      } else {
        this.addNotification({
          color: 'secondary',
          text: `Available ${this.isILR ? 'Customer' : 'Retailer'} loading, please wait...`
        })
      }
    },

    // ***
    // Helper Functions
    // ***

    determineTargetType() {
      if (this.targetBannerGroupIds.length) return this.BANNER_GROUP
      else if (this.targetMultiStoreOperatorIds.length)
        return this.MULTI_STORE_OPERATOR
      else if (this.targetStoreIds.length) return this.RETAILER_STORE
      else return this.ALL_GEOGRAPHY
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []
    },

    resetTargetGeographyForm() {
      this.targetStoreDeliveryAddressStates = []
    }
  },

  mounted() {
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
    this.mapStoresList()
    if (!this.newCreditReturnAvailabilityRule)
      this.copyCreditReturnAvailabilityRule()
    // Create blueprint of rule so that we can know if changes were made
    this.originalCreditReturnAvailabilityRule = _.cloneDeep(
      this.createBlueprint()
    )
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#add-credit-return-availability {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
